import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import ProductCard from "@/components/ProductCard";

/**
 * Public /favorites page. Hydrates the FavoritesContext list of IDs into full
 * product cards. Works for both guests (localStorage) and authed users (server).
 */
export default function FavoritesPage() {
  const { ids, ready, count } = useFavorites();
  const { user } = useAuth();
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Re-fetch when the ids set changes (covers add/remove without leaving the page).
  useEffect(() => {
    if (!ready) return;
    let aborted = false;
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        // Authed users have a dedicated server endpoint that filters to their list.
        // Guests use the public lookup so they can see their localStorage picks.
        const r = user
          ? await api.get("/favorites")
          : await api.post("/products/by-ids", { ids });
        if (aborted) return;
        const items = Array.isArray(r.data) ? r.data : [];
        // Preserve the order in which the user added the items (newest first).
        const order = new Map(ids.map((id, i) => [id, ids.length - i]));
        items.sort((a, b) => (order.get(b.id) || 0) - (order.get(a.id) || 0));
        setProducts(items);
      } catch (e) {
        if (!aborted) setProducts([]);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [ids, user, ready]);

  return (
    <div data-testid="favorites-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">
            {lang === "tr" ? "Koleksiyonum" : "My Collection"}
          </p>
          <h1 data-testid="favorites-heading" className="font-heading text-4xl lg:text-5xl">
            {lang === "tr" ? "Favorilerim" : "Favorites"}
          </h1>
          <p data-testid="favorites-count" className="text-sm text-[#52525B] mt-3">
            {count} {lang === "tr" ? "ürün" : (count === 1 ? "item" : "items")}
          </p>
        </div>

        {!user && count > 0 && (
          <Link
            to="/login"
            data-testid="favorites-signin-cta"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] underline underline-offset-8 decoration-[#C8A96A]"
          >
            {lang === "tr" ? "Giriş yap ve kaydet" : "Sign in to save"}
            <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {!ready || loading ? (
        <div className="text-center py-20 text-sm text-[#52525B]">
          {lang === "tr" ? "Yükleniyor..." : "Loading..."}
        </div>
      ) : count === 0 ? (
        <EmptyState lang={lang} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}

function EmptyState({ lang }) {
  return (
    <div data-testid="favorites-empty" className="border border-dashed border-border py-20 px-6 text-center">
      <Heart size={36} className="mx-auto text-[#C8A96A] mb-5" strokeWidth={1.4} />
      <h2 className="font-heading text-2xl mb-3">
        {lang === "tr" ? "Favori listeniz boş" : "Your wishlist is empty"}
      </h2>
      <p className="text-sm text-[#52525B] max-w-md mx-auto mb-8">
        {lang === "tr"
          ? "Beğendiğiniz ürünleri kalp ikonu ile favorilere ekleyin. Listeniz cihazınızda saklanır; giriş yaptığınızda hesabınıza taşınır."
          : "Tap the heart on any piece you love. Your list is saved on this device and synced to your account when you sign in."}
      </p>
      <Link
        to="/products"
        data-testid="favorites-browse-cta"
        className="inline-flex items-center gap-3 bg-[#C8A96A] hover:bg-[#A6864A] text-white text-xs uppercase tracking-[0.25em] px-10 py-4 transition-colors"
      >
        {lang === "tr" ? "Koleksiyonu Keşfet" : "Explore the Collection"}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}
